import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import { formatDate, formatFreightValueDisplay } from "../mockData";
import { formatCarrierQueueWaitingTime } from "../availableFreight.mapper";
import {
  HUB_TAB_AVAILABLE,
  HUB_TAB_IN_REVIEW,
} from "../carrierHub.mapper";
import {
  isPriceVisibilityMasked,
  isAnonymousShipperLabel,
  getAnonymousShipperBannerText,
} from "../../../util/freight/carrierPriceVisibility";

function DetailRow({ label, value }) {
  return (
    <Box className="lances-detail-row">
      <Typography variant="body2" className="lances-detail-row__label">
        {label}
      </Typography>
      <Typography variant="body2" className="lances-detail-row__value">
        {value || "—"}
      </Typography>
    </Box>
  );
}

function SpecItem({ icon: Icon, label, value }) {
  return (
    <Box className="lances-detail-spec">
      <span className="lances-detail-spec__icon">
        <Icon fontSize="small" />
      </span>
      <Box>
        <Typography className="lances-detail-spec__label">{label}</Typography>
        <Typography className="lances-detail-spec__value">
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

export default function FreightDetailPanel({
  freight,
  loading,
  error,
  acceptingFreightId,
  activeTab = "available",
  onClose,
  onAcceptFreight,
}) {
  if (loading) {
    return (
      <Box className="lances-detail-panel lances-detail-panel--empty">
        <CircularProgress size={32} sx={{ color: "#012443" }} />
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Carregando detalhes...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="lances-detail-panel lances-detail-panel--empty">
        <Typography color="error" align="center">
          {error}
        </Typography>
        <Button variant="text" onClick={onClose} sx={{ mt: 2 }}>
          Fechar
        </Button>
      </Box>
    );
  }

  if (!freight) {
    return (
      <Box className="lances-detail-panel lances-detail-panel--empty">
        <Typography color="text.secondary" align="center">
          Selecione um frete para ver os detalhes
        </Typography>
      </Box>
    );
  }

  const isAccepting = acceptingFreightId === freight.id;
  const isInQueue = Boolean(freight.isInCarrierQueue);
  const isMasked = isPriceVisibilityMasked(freight);
  const isMyClient = Boolean(freight.isMyClient || freight.isAffiliateShipper);
  const badgeLabel =
    freight.contractingLabel || freight.modality || "PREÇO FIXO";
  const isBidStyle =
    String(freight?.contractingType || "").toUpperCase() === "BID" ||
    (!isMyClient && !isInQueue);
  const queueWaitingTime = isInQueue
    ? formatCarrierQueueWaitingTime(freight)
    : null;
  const shipperName =
    freight.priceVisibility?.displayShipperName || freight.shipper;
  const isAnonymousShipper = isAnonymousShipperLabel(shipperName);
  const displayPrice =
    freight.priceVisibility?.displayPrice ?? freight.freightValue;
  const canManageQueue =
    activeTab === HUB_TAB_AVAILABLE || activeTab === HUB_TAB_IN_REVIEW;

  return (
    <Box
      className={[
        "lances-detail-panel",
        isMyClient ? "lances-detail-panel--client" : "",
        isBidStyle ? "lances-detail-panel--open" : "",
        isMasked && !isMyClient ? "lances-detail-panel--external" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Box className="lances-detail-panel__header">
        <Typography variant="h6" fontWeight={700}>
          Detalhes da carga
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className="lances-detail-panel__content">
        <Box className="lances-detail-panel__summary">
          {isInQueue ? (
            <Box className="lances-freight-card__candidate-strip lances-freight-card__candidate-strip--detail">
              Candidato a este frete
            </Box>
          ) : null}
          {isMasked && !isMyClient ? (
            <Box className="lances-freight-card__external-strip lances-freight-card__external-strip--detail">
              Valor Anunciado — somente visualização
            </Box>
          ) : null}
          {isAnonymousShipper ? (
            <Box className="lances-freight-card__anonymous-strip lances-freight-card__anonymous-strip--detail">
              {getAnonymousShipperBannerText(shipperName)}
            </Box>
          ) : null}

          <Box className="lances-detail-panel__badges">
            <span
              className={[
                "lances-badge",
                isBidStyle ? "lances-badge--open" : "lances-badge--fixed",
              ].join(" ")}
            >
              {badgeLabel}
            </span>
            {isMyClient ? (
              <span className="lances-badge lances-badge--client">Seu cliente</span>
            ) : null}
          </Box>

          <Typography fontWeight={700} className="lances-detail-panel__number">
            #{freight.number}
          </Typography>

          <Typography className="lances-detail-panel__route-title">
            {freight.origin} → {freight.destination}
          </Typography>

          <Box className="lances-detail-route-visual" aria-hidden>
            <span className="lances-detail-route-visual__pin lances-detail-route-visual__pin--origin">
              <FmdGoodOutlinedIcon fontSize="small" />
            </span>
            <span className="lances-detail-route-visual__line" />
            <span className="lances-detail-route-visual__truck">
              <LocalShippingOutlinedIcon fontSize="small" />
            </span>
            <span className="lances-detail-route-visual__line" />
            <span className="lances-detail-route-visual__pin lances-detail-route-visual__pin--destination">
              <FmdGoodOutlinedIcon fontSize="small" />
            </span>
          </Box>
        </Box>

        <Box className="lances-detail-spec-grid">
          <SpecItem
            icon={CalendarTodayOutlinedIcon}
            label="Data da coleta"
            value={formatDate(freight.collectionDate)}
          />
          <SpecItem
            icon={LocalShippingOutlinedIcon}
            label="Tipo de veículo"
            value={freight.vehicleType}
          />
          <SpecItem
            icon={CategoryOutlinedIcon}
            label="Tipo de carga"
            value={freight.cargoType}
          />
          <SpecItem
            icon={Inventory2OutlinedIcon}
            label="Carroceria"
            value={freight.bodyType}
          />
          <SpecItem
            icon={ScheduleOutlinedIcon}
            label="Prazo de pagamento"
            value={freight.paymentTermLabel}
          />
          <SpecItem
            icon={ApartmentOutlinedIcon}
            label="Modalidade"
            value={freight.modality || freight.contractingLabel}
          />
        </Box>

        <Typography variant="subtitle2" className="lances-detail-section-title">
          Dados do Frete
        </Typography>
        <Box className="lances-detail-section">
          <DetailRow
            label="Embarcador"
            value={
              isAnonymousShipper
                ? getAnonymousShipperBannerText(shipperName)
                : shipperName
            }
          />
          <DetailRow label="Peso" value={freight.weight} />
          <DetailRow label="Cubagem" value={freight.volume} />
          <DetailRow
            label="Data de Entrega"
            value={formatDate(freight.deliveryDate)}
          />
        </Box>

        {freight.observations?.length > 0 && (
          <>
            <Typography
              variant="subtitle2"
              className="lances-detail-section-title"
            >
              Observações
            </Typography>
            <ul className="lances-observations">
              {freight.observations.map((obs) => (
                <li key={obs}>
                  <Typography variant="body2">{obs}</Typography>
                </li>
              ))}
            </ul>
          </>
        )}

        <Typography variant="subtitle2" className="lances-detail-section-title">
          {isMasked ? "Valor Anunciado" : "Valor do Frete"}
        </Typography>
        <Typography className="lances-bid-dispute__value">
          {formatFreightValueDisplay(displayPrice, freight.pricingType)}
        </Typography>
        {isInQueue && (
          <Box className="lances-freight-card__queue-hint-wrap" sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Você está na fila aguardando contratação
            </Typography>
            {queueWaitingTime ? (
              <Typography variant="body2" color="text.secondary">
                há {queueWaitingTime}
              </Typography>
            ) : null}
          </Box>
        )}
        {canManageQueue ? (
          <Button
            variant="contained"
            fullWidth
            className={
              isInQueue
                ? "lances-btn-leave-queue"
                : isMyClient
                  ? "lances-btn-accept lances-btn-accept--client"
                  : "lances-btn-accept lances-btn-accept--open"
            }
            sx={{ mt: 2 }}
            disabled={isAccepting || (isMasked && !isInQueue)}
            onClick={() => {
              if (isMasked && !isInQueue) return;
              onAcceptFreight(freight);
            }}
          >
            {isAccepting ? (
              <CircularProgress
                size={20}
                sx={{ color: isInQueue ? "#15803d" : "#fff" }}
              />
            ) : isInQueue ? (
              "Sair da fila"
            ) : isMasked ? (
              "Somente visualização"
            ) : isMyClient ? (
              "Aceitar carga"
            ) : (
              "Entrar na fila"
            )}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
